// Tipos e formatadores da tela de carteira. O backend já devolve tudo agregado;
// aqui só existe o que a tela precisa para ler o payload e desenhar.

import { AlertTriangle, CheckCircle2, CircleSlash, Clock, PauseCircle, XCircle } from 'lucide-react';
import { STATUS_CORES } from './carteira-viz';

export type StatusPi =
  | 'obsoleto'
  | 'sem_saldo'
  | 'nunca_faturou'
  | 'parado'
  | 'atrasado'
  | 'em_dia';

export interface ImportacaoCarteira {
  id: string;
  arquivo: string;
  referencia: string;
  anoBase: number;
  observacao: string | null;
  criadoPor: string | null;
  createdAt: string;
  totalPis: number;
  totalPedidos: number;
}

export interface KpisCarteira {
  totalPis: number;
  /** Quantos desses PIs ainda têm saldo — a maioria da planilha está zerada. */
  comSaldo: number;
  saldoTotal: number;
  faturadoTotal: number;
  pendenteTotal: number;
  percentualFaturado: number;
  ativos: number;
  saldoAtivos: number;
  obsoletos: number;
  saldoObsoletos: number;
  semFaturar: number;
  saldoSemFaturar: number;
  parados: number;
  saldoParado: number;
  custoParaFinalizar: number;
  pedidosEmAberto: number;
  realizadoNoPeriodo: number;
  previstoNoPeriodo: number;
  ultimoPeriodoPrevisto: string | null;
}

export interface PontoSerie {
  periodo: string;
  rotulo: string;
  ano: number;
  mes: number | null;
  valor: number;
  acumulado: number;
  pis: number;
  /** Mês já fechado: o valor não muda mais. */
  realizado: boolean;
  /** Mês do retrato — ainda corre, então o número pode mudar até fechar. */
  emCurso: boolean;
}

export interface GrupoValor {
  chave: string;
  quantidade: number;
  saldo: number;
  faturado: number;
  pendente: number;
}

export interface GrupoPedido {
  chave: string;
  quantidade: number;
  pedido: number;
  aberto: number;
}

/** Faixa ordenada de "há quanto tempo não fatura" — a leitura de tempo da tela. */
export interface GrupoAging {
  faixa: string;
  rotulo: string;
  curto: string;
  quantidade: number;
  pendente: number;
}

export interface ResumoCarteira {
  importacao: {
    id: string;
    arquivo: string;
    referencia: string;
    anoBase: number;
    observacao: string | null;
    createdAt: string;
  } | null;
  kpis: KpisCarteira | null;
  serieMensal: PontoSerie[];
  porSquad: (GrupoValor & { squad: string })[];
  porSituacao: (GrupoValor & { situacao: string })[];
  porStatus: (GrupoValor & { status: StatusPi })[];
  porAging: GrupoAging[];
  porRegiao: (GrupoPedido & { regiao: string })[];
  porTipo: (GrupoPedido & { tipo: string })[];
  opcoes: {
    squads: string[];
    situacoes: string[];
    famComls: string[];
    regioes: string[];
  };
  alertas: {
    semPrevisao: number;
    previsaoVencida: number;
    custoAcimaDoSaldo: number;
  };
}

export interface PiLinha {
  id: string;
  codigo: string;
  descricao: string | null;
  squad: string | null;
  situacao: string | null;
  famComl: string | null;
  complemento: string | null;
  saldo: number;
  faturado: number;
  pendente: number;
  custoParaFinalizar: number | null;
  statusObs: string | null;
  acoes: string | null;
  previsao: string | null;
  previsaoTexto: string | null;
  status: StatusPi;
  mesesSemFaturar: number | null;
  faturamentos: { periodo: string; rotulo: string; valor: number }[];
}

export interface PiDetalhe extends Omit<PiLinha, 'faturado' | 'pendente' | 'status' | 'mesesSemFaturar' | 'faturamentos'> {
  /** Só os meses fechados. */
  realizado: number;
  /** Mês em curso + meses futuros programados. */
  previsto: number;
  /** saldo − realizado. */
  aFaturar: number;
  vendaLiquida: number | null;
  custoBase: number | null;
  faturamentos: {
    ano: number;
    mes: number | null;
    rotulo: string;
    valor: number;
    realizado: boolean;
    emCurso: boolean;
  }[];
  pedidos: {
    id: string;
    tipo: string;
    regiao: string | null;
    cliente: string | null;
    pedidoCliente: string | null;
    item: string | null;
    descricao: string | null;
    quantidadePedida: number;
    valorPedido: number;
    quantidadeAberta: number;
    valorAberto: number;
  }[];
}

/**
 * Metadados de cada status. O ícone e o rótulo são obrigatórios: a cor sozinha
 * não distingue "atrasado" de "parado" para quem tem visão normal, muito menos
 * sob daltonismo.
 */
export const STATUS_META: Record<StatusPi, {
  rotulo: string;
  descricao: string;
  cor: string;
  icone: typeof CheckCircle2;
}> = {
  em_dia: {
    rotulo: 'Em dia',
    descricao: 'Faturou nos últimos meses e a previsão de encerramento ainda está no futuro.',
    cor: STATUS_CORES.bom,
    icone: CheckCircle2,
  },
  atrasado: {
    rotulo: 'Atrasado',
    descricao: 'Ainda fatura, mas a previsão de encerramento já passou.',
    cor: STATUS_CORES.atencao,
    icone: Clock,
  },
  parado: {
    rotulo: 'Parado',
    descricao: 'Tem saldo a faturar e não fatura há 3 meses ou mais.',
    cor: STATUS_CORES.serio,
    icone: PauseCircle,
  },
  nunca_faturou: {
    rotulo: 'Nunca faturou',
    descricao: 'PI ativo, com saldo, e nenhum faturamento registrado na planilha.',
    cor: STATUS_CORES.critico,
    icone: XCircle,
  },
  sem_saldo: {
    rotulo: 'Sem saldo',
    descricao: 'Ativo, mas já faturou tudo o que tinha em carteira.',
    cor: '#A7A7A7',
    icone: CircleSlash,
  },
  obsoleto: {
    rotulo: 'Obsoleto',
    descricao: 'Marcado como "Totalmente Obsoleto" na planilha.',
    cor: '#A7A7A7',
    icone: AlertTriangle,
  },
};

/** Ordem em que os status aparecem em filtros e legendas: do pior para o melhor. */
export const STATUS_ORDEM: StatusPi[] = [
  'nunca_faturou', 'parado', 'atrasado', 'em_dia', 'sem_saldo', 'obsoleto',
];

const MESES_EXTENSO = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

export function moeda(valor: number): string {
  return valor.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
}

export function moedaExata(valor: number): string {
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Formato curto para eixo e stat tile: R$ 19,1 mi. */
export function moedaCurta(valor: number): string {
  const abs = Math.abs(valor);
  if (abs >= 1_000_000) return `R$ ${(valor / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;
  if (abs >= 1_000) return `R$ ${(valor / 1_000).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} mil`;
  return moeda(valor);
}

export function percentual(valor: number): string {
  return `${valor.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
}

/** "agosto de 2026" a partir do 1º dia do mês devolvido pela API. */
export function mesPorExtenso(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${MESES_EXTENSO[d.getUTCMonth()]} de ${d.getUTCFullYear()}`;
}

/** "Agosto de 2026" — para quando o mês abre a frase. */
export function mesPorExtensoCapitalizado(iso: string): string {
  const t = mesPorExtenso(iso);
  return t.charAt(0).toUpperCase() + t.slice(1);
}

export function dataCurta(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}

/** Meses sem faturar a partir dos quais o backend considera o PI "parado". */
export const MESES_PARA_PARADO = 3;
